import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { cellCenter, hexToWorld, wedgeRoute, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { companyShield, COMPANY_RED } from '../models/sendas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, SnowFall, smoothTiles, snowGround, snowUniforms } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';
import { CHASM_PLACES, ChasmPlace } from './chasm.ts';
import { FORT_PLACES, FortPlace, HALL_PLACES } from './fort.ts';
import { SLIDE_PLACES, SlidePlace } from './slide.ts';
import { PASS_PLACES, PassPlace } from './pass.ts';

/**
 * The Spine, north face (docs/sendas/guerrero.md §3: «la cara norte, de día. Roca gris azulada, viento que se ve en la
 * nieve que corre a ras del suelo, cielo blanco, sombras largas y frías. El rojo lo ponen solo los escudos»), for the
 * warrior's path: the board's grass to snow and blue-grey rock, its pines to dark firs with snow on their shoulders,
 * its bushes to drifts; a pale sky and a low cold sun; and the wind, a sheet over the ground in a shader (streaks of
 * blown snow running along it, never particles per flake, docs/SENDAS.md «Escenarios»). Each place adds its own:
 *
 * - `pines`: the Company's shields standing in the snow where Kel digs them out (gone as each is dug);
 * - `ledge`: the wedges' ground (battle/wedges.ts): the cliff and its hanging cornice, the low yard wall with its gaps
 *   and the shields planted in them, the strip under the cornice with its stakes, the loose rock on the wall, the
 *   guard house in the middle; the column's way drawn on the ground in red while the puzzle lasts (one hex wide where
 *   it narrows, brighter), the rock's shadow growing on the strip after the push, the cornice coming down;
 * - `spineFar`: the Spine seen from below at dawn, over a sea of cloud: a great ridge, and at its top the line of red
 *   dots in the snow (the cinematic's spyglass);
 * - `guardhouse`, `guardhouseFire`: the burnt guard house's yard (walls, the well, the door with the diary's page
 *   nailed to it) and its common room (a hearth, bunks, the open roof);
 * - `barracks`: the bastion's barracks yard at dawn (no snow down there).
 *
 * Draw calls: the dressing (one merged mesh), the wind (two sheets), the shields (a handful), the way (one ribbon),
 * the shadow (one), a glow layer; the pines' wood (one, no shadow), the ledge's cornice (one) and its shade (one), the
 * spyglass's ranges (one); the sky dome and the falling snow (one each, engine/snow.ts). The ground is the board's own
 * mesh with a snow material (seams closed, relief in the normal only). No lights of its own: the board's are
 * repainted, always cold (HOURS).
 */

const S = WORLD_PER_HEX;
const SNOW = new THREE.Color('#e8edf4');
const SNOW_SHADE = new THREE.Color('#b9c4d3');
const SNOW_PATH = new THREE.Color('#c8d0dc');
const ROCK = ['#6f7886', '#5f6876', '#7c8592', '#566070'];
const FIR = ['#1f2c2c', '#243230', '#1b2628', '#2a3634'];
const WOOD = ['#3a3430', '#4a4038', '#2e2925'];
const BURNT = ['#2a2828', '#363230', '#24221f'];
const STONE = ['#8a8f98', '#7a7f88', '#969ba3'];
/** The ledge's rock and its yard wall: darker and bluer than the board's, so the snow and what moves on it stand out. */
const CLIFF = ['#4a5361', '#3f4754', '#56606e', '#2f3540'];
const WALL = ['#6b7280', '#5f6672', '#767d89'];
const CORNICE_BELLY = '#9db0cb';
/** The barracks hall's stone: darker than the yard's flags, cold. */
const HALL = ['#545a64', '#686e78', '#4a4f58'];
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');

export const SPINE_PLACES = new Set(['pines', 'ledge', 'spineFar', 'guardhouse', 'guardhouseFire', 'barracks', ...CHASM_PLACES, ...FORT_PLACES, ...SLIDE_PLACES, ...PASS_PLACES]);

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/**
 * The Spine's light by the hour, always cold (docs/sendas/guerrero.md §3): steel-blue shadows from a pale low sun, a
 * fog with depth, a sky with weight. Dawn and dusk are blue hours, never rose: the only warm colour up here is the
 * Company's red. `sky` is the dome's zenith, horizon and sun.
 */
const HOURS: Record<Hour, { bg: string; fog: string; low: string; mid: string; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#a9b8cb', fog: '#a7b5c8', low: '#e2e8f0', mid: '#cdd6e2', hemi: ['#c4d4ee', '#3a4659', 2.3], key: ['#fff8f0', 3.2], rim: ['#9cc0ff', 1.6], sky: ['#4f6888', '#c2cedc', '#fff6e8'] },
  dawn: { bg: '#7f8ea6', fog: '#7c8ba2', low: '#c3cdda', mid: '#a9b5c6', hemi: ['#adbfdc', '#2a3344', 2.0], key: ['#f2f0f4', 2.7], rim: ['#8fb4ff', 1.8], sky: ['#26364f', '#a3b2c6', '#eef2fa'] },
  dusk: { bg: '#3d4b63', fog: '#46546c', low: '#64728a', mid: '#4d5b73', hemi: ['#8ea3c6', '#1a2130', 1.8], key: ['#cdd8f2', 2.0], rim: ['#7ea2ff', 1.6], sky: ['#141d2e', '#5d6c86', '#c8d4f0'] },
  night: { bg: '#0a0e16', fog: '#111824', low: '#1c2432', mid: '#121a26', hemi: ['#7084ae', '#12151c', 1.2], key: ['#a8bce8', 0.95], rim: ['#6f8fff', 1.3], sky: ['#05080e', '#141c2a', '#8fa4d8'] },
};

/** The wind over the snow: streaks of blown snow sliding along the ground, thicker in gusts. */
const windMat = (dir: THREE.Vector2, strength: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: { time: { value: 0 }, dir: { value: dir }, strength: { value: strength } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying float vFog;
      void main() {
        vec4 w = modelMatrix * vec4(position + normal * 0.03, 1.0);
        vXZ = w.xz;
        vec4 mv = viewMatrix * w;
        vFog = clamp(-mv.z / 90.0, 0.0, 1.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec2 dir;
      uniform float strength;
      varying vec2 vXZ;
      varying float vFog;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1.0, 0.0)), u.x), mix(h(i + vec2(0.0, 1.0)), h(i + vec2(1.0, 1.0)), u.x), u.y);
      }
      void main() {
        vec2 d = normalize(dir);
        vec2 side = vec2(-d.y, d.x);
        // Along the wind the streaks are long, across it thin; they slide with the wind and wander a little.
        float a = dot(vXZ, d);
        float b = dot(vXZ, side);
        float t = time;
        float s1 = n(vec2(a * 0.55 - t * 2.6, b * 4.2 + sin(a * 0.3 + t * 0.7) * 0.6));
        float s2 = n(vec2(a * 1.3 - t * 4.1, b * 9.0 + 7.0));
        float gust = 0.55 + 0.45 * n(vec2(a * 0.06 - t * 0.45, b * 0.06));
        float streak = smoothstep(0.62, 0.95, s1 * 0.7 + s2 * 0.45) * gust;
        float alpha = streak * strength * (1.0 - vFog * 0.6);
        gl_FragColor = vec4(vec3(0.96, 0.98, 1.0), alpha);
      }`,
  });

/** A sea of cloud seen from above: soft billows, slowly drifting, thinning at the edges. */
const cloudMat = (strength: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    fog: false,
    side: THREE.DoubleSide,
    uniforms: { time: { value: 0 }, strength: { value: strength } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        vec4 w = modelMatrix * vec4(position, 1.0);
        vXZ = w.xz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float strength;
      varying vec2 vXZ;
      varying vec2 vUv;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1.0, 0.0)), u.x), mix(h(i + vec2(0.0, 1.0)), h(i + vec2(1.0, 1.0)), u.x), u.y);
      }
      void main() {
        vec2 p = vXZ * 0.08 + vec2(time * 0.03, 0.0);
        float f = n(p) * 0.55 + n(p * 2.1 + 3.0) * 0.3 + n(p * 4.3 - 7.0) * 0.15;
        float edge = smoothstep(0.0, 0.25, vUv.x) * smoothstep(1.0, 0.75, vUv.x) * smoothstep(0.0, 0.3, vUv.y) * smoothstep(1.0, 0.7, vUv.y);
        float a = smoothstep(0.32, 0.72, f) * strength * edge;
        vec3 c = mix(vec3(0.58, 0.65, 0.76), vec3(0.93, 0.95, 0.99), f);
        gl_FragColor = vec4(c, a);
      }`,
  });

export class SpineLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private winds: THREE.ShaderMaterial[] = [];
  private glow = new GlowLayer(64);
  private now = 0;
  private hour: Hour = 'day';
  /** The pines: the shields in the snow by their spot (dug ones hidden). */
  private dig: { g: THREE.Group; x: number; y: number }[] = [];
  /** The pines: the shields on the stretcher, shown as each is dug. */
  private carried: THREE.Group[] = [];
  /** The ledge: each gap's planted shield, the way's ribbon, the cornice and the rock, the rock's shadow, the snow fallen. */
  private gapShields: THREE.Group[] = [];
  private way: THREE.Mesh | null = null;
  private wayMat: THREE.MeshBasicMaterial | null = null;
  private wayKey = '';
  private cornice: THREE.Group | null = null;
  private loose: THREE.Mesh | null = null;
  private shadow: THREE.Mesh | null = null;
  private shadowMat: THREE.MeshBasicMaterial | null = null;
  private fallen: THREE.Mesh | null = null;
  /** The cornice's shade on the strip (gone with it). */
  private coShade: THREE.Mesh | null = null;
  private fallAt = -1;
  private pushAt = -1;
  private stripMid = new THREE.Vector3();
  /** Where the HUD hangs its marks (world points): the gaps, the rock, the cornice's counter, the house. */
  readonly anchors: { gaps: THREE.Vector3[]; rock: THREE.Vector3 | null; cornice: THREE.Vector3 | null; house: THREE.Vector3 | null } = { gaps: [], rock: null, cornice: null, house: null };
  private sky: { bg: THREE.Color | null } = { bg: null };
  private dome: SkyDome | null = null;
  private fall: SnowFall | null = null;
  private terrain: TerrainBuild | null = null;
  /** The cut bridge's gorge (chapter 2's places), if this is one. */
  readonly chasm: ChasmPlace | null = null;
  /** The fort at the foot of the pass (chapter 3's places), if this is one. */
  readonly fort: FortPlace | null = null;
  /** The avalanche's slopes and the ice cave (chapter 4's places), if this is one. */
  readonly slide: SlidePlace | null = null;
  /** The Pass of the Rift (chapter 5's places), if this is one. */
  readonly pass: PassPlace | null = null;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `spine-${place}`;
    const kit = new Kit();
    const snowy = place !== 'barracks';
    if (place === 'ledge') this.buildLedge(kit, s);
    else if (place === 'pines') this.buildPines(s);
    else if (place === 'spineFar') this.buildFar(kit);
    else if (place === 'guardhouse' || place === 'guardhouseFire') this.buildGuardhouse(kit, place === 'guardhouseFire');
    else if (place === 'barracks') this.buildBarracks(kit);
    // The cut bridge's gorge (chapter 2): its own dressing, and the drop cut out of the board (engine/chasm.ts).
    else if (CHASM_PLACES.has(place)) {
      const c = new ChasmPlace(ctx, map, place, s, kit);
      // From the edge, far over the gorge: the Spine's crest and the pass with its ice split (and from the platform,
      // in its scene: Kel looks up at it; the fight's board doesn't carry it).
      if (place === 'chasmFar') this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, 4, -44) });
      if (place === 'span' && !s.rules?.senda?.gust) this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, 6, -52) });
      (this as { chasm: ChasmPlace | null }).chasm = c;
      this.group.add(c.group);
    }
    // The fort at the foot of the pass (chapter 3): its own dressing (engine/fort.ts); from outside, the Spine's crest
    // and the pass above it to the north.
    else if (FORT_PLACES.has(place)) {
      const f = new FortPlace(ctx, map, place, s, kit);
      (this as { fort: FortPlace | null }).fort = f;
      this.group.add(f.group);
      if (place === 'fortFar' || place === 'fortClimb' || place === 'fort' || place === 'fortPast') this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, 3, place === 'fortClimb' ? -30 : -36) });
      // Across the valley to the south, the slope of the high farms (their lights are the fort's).
      if (place === 'fortFar' || place === 'fort' || place === 'fortPast') this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, -9, 72), yaw: Math.PI, low: true });
    }
    // The avalanche's slopes and the ice cave (chapter 4): their own dressing (engine/slide.ts); from the slope, the
    // pass above it to the north, its ice split.
    else if (SLIDE_PLACES.has(place)) {
      const sl = new SlidePlace(ctx, map, place, s, kit);
      (this as { slide: SlidePlace | null }).slide = sl;
      this.group.add(sl.group);
      if (place === 'slideFar' || place === 'loose') this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, place === 'slideFar' ? 7 : 4, -34) });
      if (place === 'avalanche' || place === 'caveMouth') this.buildFar(kit, { dots: false, at: new THREE.Vector3(0, 5, -40) });
    }
    // The Pass of the Rift (chapter 5): its own dressing (engine/pass.ts); from the ledge, the crest above.
    else if (PASS_PLACES.has(place)) {
      const ps = new PassPlace(ctx, map, place, s, kit);
      (this as { pass: PassPlace | null }).pass = ps;
      this.group.add(ps.group);
      if (place === 'crag') this.buildFar(kit, { dots: false, at: new THREE.Vector3(-6, 9, -40) });
    }
    // A few firs and rocks round the edge of the board, so even a bare clearing reads as the Spine.
    if (snowy && place !== 'spineFar' && place !== 'pines' && place !== 'fortClimb' && !this.chasm && !HALL_PLACES.has(place) && !this.slide && !this.pass) this.edgeDressing(kit);
    if (place === 'pines') this.buildWood(s);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'spine-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- the board, repainted ---------- */

  /** The grass to snow, the firs dark with snow on top, the bushes to drifts, the wind over all of it; the light. */
  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    const snowy = this.place !== 'barracks';
    if (!snowy) {
      // The barracks yard: cold grey flagstones, frost on the grass beyond, no trees in the bastion.
      const c = new THREE.Color();
      const tint = (mesh: THREE.Mesh, flag: boolean): void => {
        const col = mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        if (!col) return;
        for (let i = 0; i < col.count; i++) {
          const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
          if (flag) c.set(lum < 0.05 ? '#2a2e35' : STONE[i % 3] as string).multiplyScalar(0.7 + lum * 0.6);
          else c.set('#6b7366').lerp(new THREE.Color('#b9c2cc'), 0.35).multiplyScalar(0.7 + lum * 0.5);
          col.setXYZ(i, c.r, c.g, c.b);
        }
        col.needsUpdate = true;
      };
      tint(terrain.mesh, true);
      terrain.decor.traverse((o) => {
        if (o.name === 'outskirts-ground') tint(o as THREE.Mesh, false);
        if ((o as THREE.InstancedMesh).isInstancedMesh && (o.name === 'trunks' || o.name === 'cones' || o.name === 'blobs' || o.name === 'tufts')) o.visible = false;
      });
    }
    if (snowy) {
      const c = new THREE.Color();
      const p = new THREE.Vector3();
      const tint = (mesh: THREE.Mesh, road: boolean): void => {
        const geo = mesh.geometry;
        const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
        const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
        if (!col || !pos) return;
        mesh.updateMatrixWorld(true);
        for (let i = 0; i < pos.count; i++) {
          p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
          const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
          const n = 0.5 + 0.5 * Math.sin(p.x * 0.9 + p.z * 0.4) * Math.cos(p.z * 0.7 - p.x * 0.3);
          if (road) c.copy(SNOW_PATH).multiplyScalar(0.92 + lum * 0.25);
          else c.copy(SNOW).lerp(SNOW_SHADE, Math.min(1, Math.max(0, 0.75 - lum * 1.4)) * 0.8 + n * 0.12);
          // Crests blown bare: blue-grey rock shows through.
          if (!road && p.y > 0.42) c.lerp(new THREE.Color(ROCK[1] as string), Math.min(0.75, (p.y - 0.42) * 1.6));
          col.setXYZ(i, c.r, c.g, c.b);
        }
        col.needsUpdate = true;
      };
      tint(terrain.mesh, false);
      // One snowfield, not a floor of tiles: the seams closed and the ground painted in a shader (engine/snow.ts).
      smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
      // The common room's floor is stone with a little snow blown in, not a snowfield: the fire reads warm on it.
      const room = this.place === 'guardhouseFire' || HALL_PLACES.has(this.place);
      // The ice cave's floor: frost on blue ice, never white snow.
      const ice = this.place === 'iceCave';
      const su = snowUniforms(room ? { snow: '#6a707a', shade: '#3e434c', rock: '#2c3038' } : ice ? { snow: '#9fbad3', shade: '#5f7f9f', rock: '#33506e' } : { snow: '#eef2f8', shade: '#9fb0c8', rock: ROCK[3] as string }, (this.map.radius + 0.5) * S, new THREE.Vector2(0.85, 0.35));
      if (this.place === 'ledge' || this.chasm) (su.uRockIn as THREE.IUniform<number>).value = 0.45;
      if (this.place === 'pines') (su.uRockIn as THREE.IUniform<number>).value = 0.15;
      const ground = snowGround(terrain.mesh.material as THREE.MeshStandardMaterial, su, false);
      this.mats.push(ground);
      terrain.mesh.material = ground;
      let outMat: THREE.MeshStandardMaterial | null = null;
      terrain.decor.traverse((o) => {
        if (o.name === 'outskirts-ground') {
          tint(o as THREE.Mesh, false);
          const mesh = o as THREE.Mesh;
          outMat ??= snowGround(mesh.material as THREE.MeshStandardMaterial, su, true);
          mesh.material = outMat;
        }
        else if (o.name === 'roads') tint(o as THREE.Mesh, true);
        const im = o as THREE.InstancedMesh;
        if (!im.isInstancedMesh) return;
        if (o.name === 'tufts') o.visible = false;
        // The pass is a gorge between the plates: no wood beyond its walls.
        else if (this.pass && (o.name === 'trunks' || o.name === 'cones' || o.name === 'blobs')) o.visible = false;
        else this.recolorInstances(im);
      });
      if (outMat) this.mats.push(outMat);
      // The gorge: the drop cut out (after the seams are closed), and on the platform the gusts' own sheet.
      if (this.chasm) {
        this.chasm.carve(terrain);
        if (this.place === 'span') this.chasm.gustSheet(terrain.mesh);
      }
      // The avalanche's shadow and snow, and the rocks' lee, painted on the board's own ground.
      this.slide?.sheetOn(terrain.mesh);
      // The ledge of the wind: the drop cut out, and the gusts' sheet on the ledge.
      if (this.pass) {
        this.pass.carve(terrain);
        this.pass.sheetOn(terrain.mesh);
      }
      // The wind: a sheet over the board and one over the ground beyond it, the same streaks (on the platform the
      // gusts' sheet runs on the board instead).
      const dir = new THREE.Vector2(0.85, 0.35);
      for (const src of [...(this.place === 'span' ? [] : [terrain.mesh]), ...this.findGround(terrain)]) {
        const m = windMat(dir, this.place === 'guardhouseFire' || HALL_PLACES.has(this.place) || this.place === 'iceCave' ? 0.08 : 0.42);
        this.winds.push(m);
        this.mats.push(m);
        const sheet = new THREE.Mesh(src.geometry, m);
        sheet.matrixAutoUpdate = false;
        src.updateMatrixWorld(true);
        sheet.matrix.copy(src.matrixWorld);
        sheet.renderOrder = 2;
        sheet.name = 'spine-wind';
        sheet.frustumCulled = false;
        this.group.add(sheet);
      }
    }
    // The sky over all of it, and the snow coming down (lighter on the phone: fewer, the same look).
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    if (snowy && this.place !== 'guardhouseFire' && !HALL_PLACES.has(this.place) && this.place !== 'iceCave') {
      this.fall = new SnowFall(this.ctx.low ? 450 : 1100, new THREE.Vector2(0.85, 0.35), this.place === 'spineFar' ? 0.6 : 1);
      this.group.add(this.fall.points);
    }
    this.terrain = terrain;
    this.paint(hour);
  }

  private findGround(terrain: TerrainBuild): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground') out.push(o as THREE.Mesh);
    });
    return out;
  }

  /** Firs dark with snow on their upper tiers; broadleaf crowns and bushes to snow drifts; boulders blue-grey. */
  private recolorInstances(im: THREE.InstancedMesh): void {
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const c = new THREE.Color();
    for (let i = 0; i < im.count; i++) {
      im.getMatrixAt(i, m);
      p.setFromMatrixPosition(m);
      const k = (Math.sin(p.x * 12.9 + p.z * 78.2) * 0.5 + 0.5) * 0.999;
      if (im.name === 'cones') {
        // Higher tiers carry more snow.
        const up = Math.min(1, Math.max(0, (p.y - 0.45) * 0.9));
        c.set(FIR[Math.floor(k * FIR.length)] as string).lerp(SNOW, 0.18 + up * 0.55);
      } else if (im.name === 'blobs') c.copy(SNOW).lerp(SNOW_SHADE, k * 0.5);
      else if (im.name === 'rocks') c.set(ROCK[Math.floor(k * ROCK.length)] as string);
      else if (im.name === 'trunks') c.set(WOOD[Math.floor(k * WOOD.length)] as string).multiplyScalar(0.8);
      else continue;
      im.setColorAt(i, c);
    }
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
  }

  /** The hour's light (the cinematics call it again with their own). */
  paint(hour: Hour): void {
    this.hour = hour;
    const look = HOURS[hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    this.sky.bg = scene.background;
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set(look.fog);
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    // The fort's opening (docs/sendas/guerrero.md, chapter 3: «Atardecer naranja sobre el hielo»): the one sky up here
    // with warmth in it, a low ember band under the cold (docs/DECISIONS.md).
    if (this.place === 'fortFar' && hour === 'dusk') this.dome?.set('#1a2236', '#b4664a', '#ffb07a');
    else this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    this.terrain?.fog?.tint({ low: look.low, mid: look.mid, far: look.bg, alpha: 1 });
  }

  /* ---------- the places ---------- */

  private w(x: number, y: number, h = 0): THREE.Vector3 {
    return this.ctx.at(x, y, h);
  }

  private hexAt(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.w(c.x, c.y, h);
  }

  /** Firs and boulders in a ring just past the board's edge. */
  private edgeDressing(kit: Kit): void {
    const cone = new THREE.ConeGeometry(0.5, 1, 7);
    const trunk = new THREE.CylinderGeometry(0.06, 0.08, 1, 5);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    // The pines are a wood: two rings of firs, thick; elsewhere one, thinner.
    const wood = this.place === 'pines';
    const R = (this.map.bastion + (wood ? 0.9 : 1.4)) * S;
    const n = wood ? 64 : 26;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.sin(i * 7.3) * 0.08;
      const r = R + (Math.sin(i * 3.1) * 0.5 + 0.5) * (wood ? 3.2 : 1.6) + (wood && i % 2 ? 1.4 : 0);
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      const g = this.ctx.ground(x, z);
      if (i % (wood ? 7 : 3) === 2) {
        kit.add(rock, ROCK[i % ROCK.length] as string, place(x, g + 0.2, z, i, new THREE.Vector3(0.9, 0.6, 0.8)));
        kit.add(rock, SNOW, place(x, g + 0.45, z, i + 1, new THREE.Vector3(0.7, 0.18, 0.6)));
        continue;
      }
      const tall = 1.6 + (Math.sin(i * 5.7) * 0.5 + 0.5) * 1.2;
      kit.add(trunk, WOOD[i % 3] as string, place(x, g + 0.25, z, 0, new THREE.Vector3(1, 0.5, 1)));
      for (let t = 0; t < 3; t++) {
        const k = 1 - t * 0.27;
        const y = g + 0.4 + t * tall * 0.26;
        kit.add(cone, FIR[(i + t) % FIR.length] as string, place(x, y + tall * 0.2, z, i + t, new THREE.Vector3(0.95 * k, tall * 0.42 * k, 0.95 * k)));
        // Snow on the tier's shoulders.
        kit.add(cone, SNOW, place(x, y + tall * 0.2 + tall * 0.12 * k, z, i + t, new THREE.Vector3(0.62 * k, tall * 0.16 * k, 0.62 * k)));
      }
    }
    cone.dispose();
    trunk.dispose();
    rock.dispose();
  }

  /** The pines: the Company's shields standing in the snow at Kel's spots; a mound of snow round each. */
  private buildPines(s: BattleState): void {
    const r = s.rules?.senda;
    const i = r?.npcs.findIndex((n) => n.does === 'work') ?? -1;
    const spots = i >= 0 ? (r?.npcs[i]?.spots ?? []) : [];
    const mound = new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    this.geos.push(mound);
    const snowM = new THREE.MeshStandardMaterial({ color: SNOW, roughness: 0.95, flatShading: true });
    this.mats.push(snowM);
    spots.forEach((sp, k) => {
      const g = new THREE.Group();
      const p = this.w(sp.x, sp.y);
      g.position.copy(p);
      g.rotation.y = k * 1.7 + 0.4;
      // Big enough and leaning back far enough to read from the board's camera: the red cross faces up.
      const sh = companyShield(g, 1.35);
      sh.position.set(0, 0.26, 0);
      sh.rotation.x = -0.75 - k * 0.06;
      sh.rotation.z = 0.12 * (k - 1);
      const m = new THREE.Mesh(mound, snowM);
      m.scale.set(1.0, 0.3, 0.85);
      g.add(m);
      // A dark ring of dug, trodden snow round it, so the spot reads before the shield does.
      const ring = new THREE.Mesh(this.ringGeo(), this.ringMat());
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.025;
      g.add(ring);
      this.group.add(g);
      this.dig.push({ g, x: sp.x, y: sp.y });
    });
  }

  private ringG: THREE.BufferGeometry | null = null;
  private ringM: THREE.MeshBasicMaterial | null = null;
  private ringGeo(): THREE.BufferGeometry {
    if (!this.ringG) {
      this.ringG = new THREE.RingGeometry(0.42, 0.62, 20);
      this.geos.push(this.ringG);
    }
    return this.ringG;
  }
  private ringMat(): THREE.MeshBasicMaterial {
    if (!this.ringM) {
      this.ringM = new THREE.MeshBasicMaterial({ color: '#7d8da6', transparent: true, opacity: 0.55, depthWrite: false });
      this.mats.push(this.ringM);
    }
    return this.ringM;
  }

  /**
   * The pines below: a dense dark wood shutting the clearing in (three deep, taller to the back), the ground rising
   * behind it in white swells, boulders at its foot; and at the wood's edge by Wenn's mule, the stretcher where the
   * dug shields go, one by one. Its own merged mesh: the wood casts no shadow (the board's sun only reaches the
   * clearing), so it costs one draw call and no shadow pass.
   */
  private buildWood(s: BattleState): void {
    const kit = new Kit();
    const cone = new THREE.ConeGeometry(0.5, 1, 7);
    const trunk = new THREE.CylinderGeometry(0.06, 0.09, 1, 5);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const hill = new THREE.SphereGeometry(1, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const R0 = (this.map.bastion + 0.75) * S;
    const low = this.ctx.low;
    const rows = low ? [46, 54, 40] : [64, 76, 84, 60];
    let i = 0;
    rows.forEach((n, row) => {
      for (let k = 0; k < n; k++, i++) {
        const h1 = Math.sin(i * 12.9898) * 43758.5453;
        const j = h1 - Math.floor(h1);
        const h2 = Math.sin(i * 78.233) * 12345.678;
        const jj = h2 - Math.floor(h2);
        const a = ((k + j * 0.8) / n) * Math.PI * 2 + row * 0.37;
        const r = R0 + row * 1.25 * S * 0.75 + (jj - 0.5) * 1.1;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        const g = this.ctx.ground(x, z) + row * 0.22;
        // A boulder now and then at the wood's foot.
        if (row === 0 && k % 9 === 4) {
          kit.add(rock, ROCK[k % ROCK.length] as string, place(x, g + 0.25, z, k, new THREE.Vector3(1.1, 0.7, 1.0)));
          kit.add(rock, SNOW_SHADE, place(x, g + 0.55, z, k + 1, new THREE.Vector3(0.8, 0.2, 0.7)));
          continue;
        }
        const tall = 2.2 + row * 0.55 + jj * 1.6;
        const wide = 0.95 + j * 0.35;
        const tiers = low ? 3 : 4;
        kit.add(trunk, WOOD[i % 3] as string, place(x, g + 0.3, z, 0, new THREE.Vector3(1, 0.6, 1)));
        for (let t = 0; t < tiers; t++) {
          const f = 1 - t / (tiers + 0.4);
          const y = g + 0.45 + (t / tiers) * tall * 0.78;
          const ht = tall * 0.38 * (0.75 + f * 0.35);
          kit.add(cone, FIR[(i + t) % FIR.length] as string, place(x, y + ht * 0.5, z, i + t, new THREE.Vector3(wide * f, ht, wide * f)));
          // Snow on the tier's shoulders, on the near side mostly.
          kit.add(cone, t === tiers - 1 ? SNOW : SNOW_SHADE, place(x, y + ht * 0.62, z, i + t, new THREE.Vector3(wide * f * 0.66, ht * 0.4, wide * f * 0.66)));
        }
      }
    });
    // The ground rising behind the wood: long white swells, a few rocks breaking out.
    for (let k = 0; k < 14; k++) {
      const a = (k / 14) * Math.PI * 2 + 0.2;
      const r = R0 + 6.5 * S + Math.sin(k * 2.7) * 1.5;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      const g = this.ctx.ground(x, z);
      kit.add(hill, k % 2 ? SNOW : SNOW_SHADE, place(x, g - 0.4, z, a, new THREE.Vector3(5 + (k % 3) * 1.4, 2.4 + (k % 4) * 0.7, 3.4)));
      if (k % 3 === 0) kit.add(rock, ROCK[3] as string, place(x * 0.98, g + 1.2, z * 0.98, k, new THREE.Vector3(1.6, 1.2, 1.3)));
    }
    // The stretcher at the wood's edge by the mule: two poles, the slats, the dug shields laid on it.
    const wenn = s.rules?.senda?.npcs.find((n) => n.does === 'wait');
    if (wenn) {
      const wp = this.w(wenn.x, wenn.y);
      const toC = new THREE.Vector3(-wp.x, 0, -wp.z).normalize();
      const at = wp.clone().addScaledVector(toC, 0.95).addScaledVector(new THREE.Vector3(-toC.z, 0, toC.x), 0.55);
      const yaw = Math.atan2(toC.x, toC.z) + 0.4;
      const box = new THREE.BoxGeometry(1, 1, 1);
      const gy = this.ctx.ground(at.x, at.z);
      const off = (lx: number, lz: number): [number, number] => [at.x + Math.cos(yaw) * lx + Math.sin(yaw) * lz, at.z - Math.sin(yaw) * lx + Math.cos(yaw) * lz];
      for (const sx of [-0.28, 0.28]) {
        const [x, z] = off(sx, 0);
        kit.add(box, WOOD[1] as string, place(x, gy + 0.08, z, yaw, new THREE.Vector3(0.06, 0.06, 1.9)));
      }
      for (let k = 0; k < 5; k++) {
        const [x, z] = off(0, -0.6 + k * 0.3);
        kit.add(box, WOOD[k % 3] as string, place(x, gy + 0.12, z, yaw, new THREE.Vector3(0.6, 0.03, 0.12)));
      }
      box.dispose();
      for (let k = 0; k < 3; k++) {
        const [x, z] = off(0, -0.45 + k * 0.45);
        const g = new THREE.Group();
        g.position.set(x, gy + 0.16 + k * 0.02, z);
        g.rotation.set(-Math.PI / 2 + 0.1, 0, 0, 'YXZ');
        g.rotation.y = yaw + Math.PI;
        companyShield(g, 0.95);
        g.visible = false;
        this.group.add(g);
        this.carried.push(g);
      }
    }
    cone.dispose();
    trunk.dispose();
    rock.dispose();
    hill.dispose();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const wood = kit.build(mat, 'spine-wood');
    if (wood) {
      wood.receiveShadow = true;
      this.geos.push(wood.geometry);
      this.group.add(wood);
    }
  }

  /** The Spine from below: a great ridge beyond the board over a sea of cloud, a red line of shields near its top. */
  private buildFar(_kit: Kit, opts: { dots: boolean; at?: THREE.Vector3; yaw?: number; low?: boolean } = { dots: true }): void {
    // Drawn round the board's middle; the gorge's cinematic sets it far off to the north, over the far plate (the
    // fort's turns one round to the south, lowered: the far side of the valley, where the farms are).
    const into = new THREE.Group();
    if (opts.at) into.position.copy(opts.at);
    if (opts.yaw) into.rotation.y = opts.yaw;
    if (opts.low) into.scale.set(1.2, 0.6, 0.7);
    this.group.add(into);
    // Far off: its own material, out of the board's fog (the haze is painted into its colours instead, layer by
    // layer: the far ranges melt into the sky's horizon).
    const kit = new Kit();
    const haze = new THREE.Color(HOURS.dawn.sky[1]);
    const hz = (c: THREE.ColorRepresentation, k: number): THREE.Color => new THREE.Color(c).lerp(haze, k);
    const z0 = -18 * S;
    // The ranges: a jagged curtain each, sloping back from its foot to its crest, snow on the crests and down the
    // gullies, dark rock between. The nearest carries the pass (its notch under the saddle, its two summits beside).
    const ranges: { z: number; depth: number; base: number; haze: number; peaks: [number, number, number][]; jag: number; seed: number }[] = [
      { z: z0 - 46, depth: 12, base: 11, haze: 0.5, jag: 2.4, seed: 7.1, peaks: [[-40, 24, 9], [-16, 26.5, 8], [3, 23, 7], [14, 25.5, 9], [38, 23, 8]] },
      { z: z0 - 24, depth: 10, base: 7, haze: 0.3, jag: 2.0, seed: 3.3, peaks: [[-32, 19, 7], [-19, 21, 6], [21, 20, 7], [33, 18, 6]] },
      { z: z0 + 2, depth: 7, base: 5, haze: 0.04, jag: 1.4, seed: 1.7, peaks: [[-6.2, 17.5, 4.2], [7.4, 16.8, 4.4], [-17, 13.5, 5], [19, 14, 5], [-28, 11, 6], [29, 11.5, 6]] },
    ];
    // Merged on their own, not through the Kit: Kit.add paints each piece one colour and would lose the ranges' own
    // per-vertex snow and rock (docs/RENDIMIENTO.md, «Trampas»).
    const rangeGeos: THREE.BufferGeometry[] = [];
    const tri = (x: number): number => Math.abs((((x % 1) + 1) % 1) - 0.5) * 2;
    for (const L of ranges) {
      const near = L === ranges[2];
      const crest = (x: number): number => {
        let h = L.base + 3;
        for (const [px, ph, w] of L.peaks) h = Math.max(h, L.base + (ph - L.base) * Math.exp(-(((x - px) / w) ** 2)));
        // The saw of the ridge: sharp teeth of a few sizes.
        h += L.jag * (tri(x * 0.21 + L.seed) * 0.9 + tri(x * 0.57 + L.seed * 2.1) * 0.45 + tri(x * 1.43 + L.seed * 3.7) * 0.2 - 0.8);
        // The pass: the nearest range dips to the saddle between its two summits.
        if (near) h = Math.min(h, 10.0 + Math.abs(x - 0.5) * 0.9 + (Math.abs(x - 0.5) > 3.5 ? 40 : 0));
        return h;
      };
      const nx = near ? 150 : 110;
      const ny = 10;
      const x0 = -62;
      const x1 = 62;
      const pos: number[] = [];
      const col: number[] = [];
      const idx: number[] = [];
      const rock = new THREE.Color();
      const c = new THREE.Color();
      for (let i = 0; i <= nx; i++) {
        const x = x0 + ((x1 - x0) * i) / nx;
        const top = crest(x);
        // Ribs and gullies down the face: the face bulges and sinks along x.
        const rib = Math.sin(x * 0.9 + L.seed) * 0.5 + Math.sin(x * 2.3 + L.seed * 1.7) * 0.25;
        for (let j = 0; j < ny; j++) {
          const t = j / (ny - 1);
          const xj = j > 0 && j < ny - 1 ? Math.sin(i * 7.7 + j * 3.1) * 0.35 : 0;
          const y = L.base - 4 + (top - (L.base - 4)) * t + (j > 0 && j < ny - 1 ? Math.sin(i * 3.1 + j * 1.7) * 0.25 : 0);
          const z = L.z + L.depth * 0.5 - t * L.depth + rib * (1 - t) * 1.6 + (j === ny - 1 ? 0 : Math.sin(i * 1.3 + j) * 0.3);
          pos.push(x + xj, y, z);
          // Snow on the upper face and caught in the gullies; the ribs bare, darker to the foot.
          const gully = Math.sin(x * 0.9 + L.seed) < -0.35;
          const line = 0.74 + Math.sin(x * 0.37 + L.seed) * 0.1 + Math.sin(x * 1.9) * 0.05;
          // Under the pass the face is bare: the saddle's snow reads against dark rock.
          const bare = near && Math.abs(x - 0.5) < 5 && j < ny - 1;
          const snowy = !bare && (t > line || (gully && t > 0.45) || j === ny - 1);
          rock.set(CLIFF[(i + j) % 4] as string).multiplyScalar(0.5 + t * 0.3);
          if (snowy) c.copy(SNOW).lerp(SNOW_SHADE, gully ? 0.5 : (i + j) % 2 ? 0.35 : 0.08);
          else c.copy(rock);
          c.lerp(haze, L.haze);
          col.push(c.r, c.g, c.b);
        }
      }
      for (let i = 0; i < nx; i++)
        for (let j = 0; j < ny - 1; j++) {
          const a = i * ny + j;
          const b = (i + 1) * ny + j;
          idx.push(a, b, a + 1, b, b + 1, a + 1);
        }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      g.setIndex(idx);
      const flat = g.toNonIndexed();
      g.dispose();
      flat.computeVertexNormals();
      rangeGeos.push(flat);
    }
    const ranges3 = mergeGeometries(rangeGeos, false);
    for (const g of rangeGeos) g.dispose();
    // The pass between the two highest: the saddle, its snowfield, and on it the line of shields (red dots).
    const sx = 0.5;
    const sy = 11.6;
    const sz = z0 - 3.2;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dome = new THREE.SphereGeometry(1, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    // The saddle: a snowfield slung between the two peaks, its lip toward us.
    const rockG = new THREE.DodecahedronGeometry(1, 0);
    // A buttress of dark rock under the saddle, down into the mass below it.
    const butt = new THREE.DodecahedronGeometry(1, 1);
    kit.add(butt, hz(ROCK[1] as string, 0.18), place(sx, sy - 3.6, sz - 0.2, 0.2, new THREE.Vector3(5.4, 3.4, 2.4)));
    kit.add(butt, hz(ROCK[3] as string, 0.22), place(sx - 2.6, sy - 5.4, sz + 0.4, 1.2, new THREE.Vector3(3.6, 3.2, 2.2)));
    kit.add(butt, hz(ROCK[0] as string, 0.22), place(sx + 2.8, sy - 5.6, sz + 0.3, 2.2, new THREE.Vector3(3.8, 3.0, 2.2)));
    butt.dispose();
    const lump = new THREE.SphereGeometry(1, 14, 8);
    kit.add(lump, hz(SNOW, 0.06), place(sx, sy - 0.75, sz + 0.5, 0, new THREE.Vector3(5.2, 1.0, 2.0)));
    kit.add(lump, hz(SNOW_SHADE, 0.08), place(sx - 3.2, sy - 0.6, sz + 0.2, 0.4, new THREE.Vector3(2.6, 1.2, 1.8)));
    kit.add(lump, hz(SNOW_SHADE, 0.08), place(sx + 3.4, sy - 0.65, sz + 0.1, -0.3, new THREE.Vector3(2.8, 1.25, 1.8)));
    lump.dispose();
    kit.add(rockG, hz(SNOW_SHADE, 0.1), place(sx - 2.4, sy - 0.9, sz + 1.6, 1.1, new THREE.Vector3(1.6, 0.7, 0.9)));
    kit.add(rockG, hz(SNOW_SHADE, 0.1), place(sx + 2.8, sy - 1.0, sz + 1.5, 2.1, new THREE.Vector3(1.4, 0.6, 0.8)));
    // Boulders and dark rock down the lower slopes.
    for (let i = 0; i < (opts.dots ? 18 : 0); i++) {
      const x = -24 + i * 2.8 + Math.sin(i * 3.7) * 1.2;
      const z = z0 + 5 + Math.sin(i * 1.9) * 2.5;
      kit.add(rockG, hz(ROCK[i % ROCK.length] as string, 0.12), place(x, 0.4 + Math.abs(Math.sin(i)) * 1.2, z, i, new THREE.Vector3(1.6 + (i % 3) * 0.6, 1.4 + (i % 4) * 0.5, 1.4)));
    }
    rockG.dispose();
    // The ice that shuts the pass, split black from end to end (the cut bridge's cinematic), in place of the shields.
    if (!opts.dots) {
      // A wall of old ice across the saddle, taller than the shoulders' snow, pale blue; the split down its whole
      // length, black and crooked, wide enough to read from the bastion's side of the gorge.
      kit.add(box, hz('#bcd4ea', 0.05), place(sx, sy + 1.5, sz + 1.2, 0, new THREE.Vector3(8.2, 3.6, 1.1)));
      kit.add(box, hz('#e8f2fb', 0.05), place(sx, sy + 3.35, sz + 1.25, 0, new THREE.Vector3(8.4, 0.3, 1.2)));
      for (let i = 0; i < 6; i++) kit.add(box, hz('#9fbbd6', 0.05), place(sx - 3.5 + i * 1.4, sy + 1.4, sz + 1.78, 0, new THREE.Vector3(0.08, 3.2, 0.05), 0, Math.sin(i * 3.1) * 0.08));
      // The split: one crooked line, segment to segment, end to end of the wall.
      let px = sx - 4.1;
      let py = sy + 1.4;
      for (let i = 1; i <= 14; i++) {
        const nx = sx - 4.1 + (8.2 * i) / 14;
        const ny = sy + 1.4 + Math.sin(i * 2.1) * 0.55 + Math.sin(i * 5.3) * 0.25;
        const len = Math.hypot(nx - px, ny - py);
        kit.add(box, '#05070a', place((px + nx) / 2, (py + ny) / 2, sz + 1.8, 0, new THREE.Vector3(len + 0.06, 0.2 + (i % 3) * 0.06, 0.06), 0, Math.atan2(ny - py, nx - px)));
        px = nx;
        py = ny;
      }
    }
    for (let i = 0; i < (opts.dots ? 11 : 0); i++) {
      // Not a fence: where each fell, a little up or down the snow, unevenly spaced.
      const x = sx - 3.7 + i * 0.72 + Math.sin(i * 2.1) * 0.22;
      const y = sy + 0.1 - Math.abs(i - 5) * 0.06 + Math.sin(i * 4.7) * 0.06;
      const z = sz + 2.0 - Math.abs(i - 5) * 0.05 + Math.sin(i * 3.3) * 0.35;
      kit.add(box, '#9aa0a8', place(x, y + 0.02, z, 0, new THREE.Vector3(0.26, 0.38, 0.06)));
      kit.add(box, COMPANY_RED, place(x, y + 0.07, z + 0.04, 0, new THREE.Vector3(0.27, 0.09, 0.03)));
      kit.add(box, COMPANY_RED, place(x, y + 0.03, z + 0.045, 0, new THREE.Vector3(0.09, 0.36, 0.03)));
    }
    box.dispose();
    dome.dispose();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0, flatShading: true, fog: false });
    this.mats.push(mat);
    const ridge = kit.build(mat, 'spine-ridge');
    if (ranges3) {
      const rm = new THREE.Mesh(ranges3, mat);
      rm.name = 'spine-ranges';
      rm.frustumCulled = false;
      this.geos.push(ranges3);
      into.add(rm);
    }
    if (ridge) {
      ridge.frustumCulled = false;
      this.geos.push(ridge.geometry);
      into.add(ridge);
    }
    this.anchors.cornice = new THREE.Vector3(sx, sy, sz + 2).add(into.position);
    // A sea of cloud over the lower slopes: soft sheets drifting slowly.
    const cloud = new THREE.PlaneGeometry(240, 150, 1, 1);
    this.geos.push(cloud);
    for (let k = 0; k < (opts.dots ? 3 : 0); k++) {
      const m = cloudMat(0.85 - k * 0.18);
      this.winds.push(m);
      this.mats.push(m);
      const sheet = new THREE.Mesh(cloud, m);
      sheet.rotation.x = -Math.PI / 2;
      sheet.position.set(0, 4.2 + k * 1.2, z0 + 50 - k * 4);
      sheet.renderOrder = 3;
      sheet.frustumCulled = false;
      into.add(sheet);
    }
  }

  /** The bastion's barracks yard at dawn: the curtain wall behind, a rack of shields, a cart, barrels; no snow down here. */
  private buildBarracks(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    // The curtain wall in an arc behind the barracks, crenellated.
    const R = 7.4 * S;
    for (let i = 0; i < 24; i++) {
      const a = -Math.PI / 2 + ((i - 11.5) / 24) * 2.6;
      const x = Math.cos(a) * R;
      const z = Math.sin(a) * R;
      const g = this.ctx.ground(x, z);
      const yaw = -a - Math.PI / 2;
      kit.add(box, STONE[i % 3] as string, place(x, g + 1.7, z, yaw, new THREE.Vector3(1.05, 3.4, 0.9)));
      if (i % 2 === 0) kit.add(box, STONE[(i + 1) % 3] as string, place(x, g + 3.6, z, yaw, new THREE.Vector3(0.5, 0.42, 0.92)));
    }
    // A rack of shields by the barracks' door, barrels, a cart.
    const rx = 2.4;
    const rz = -1.2;
    const g0 = this.ctx.ground(rx, rz);
    kit.add(box, WOOD[0] as string, place(rx, g0 + 0.5, rz, 0.3, new THREE.Vector3(1.4, 0.08, 0.1)));
    for (let k = 0; k < 4; k++) {
      kit.add(box, '#3e4a5e', place(rx - 0.5 + k * 0.34, g0 + 0.42, rz + 0.08, 0.3, new THREE.Vector3(0.28, 0.4, 0.05)));
      kit.add(box, '#8c96a4', place(rx - 0.5 + k * 0.34, g0 + 0.42, rz + 0.11, 0.3, new THREE.Vector3(0.06, 0.4, 0.01)));
    }
    for (let k = 0; k < 3; k++) kit.add(cyl, WOOD[k % 3] as string, place(-2.6 + k * 0.5, this.ctx.ground(-2.6, -1) + 0.3, -1 - (k % 2) * 0.4, 0, new THREE.Vector3(0.42, 0.6, 0.42)));
    // The barracks hall behind the yard: long, two storeys of grey stone, a steep slate roof, a row of dark windows,
    // buttresses, the door under a red-trimmed lintel; a round tower at its end and the curtain's walkway beyond. Big
    // enough to be the garrison's house, not a shed.
    const hz = -4.6;
    const hw = 8.4;
    const hd = 2.8;
    const hh = 2.9;
    const gh = this.ctx.ground(0, hz);
    kit.add(box, HALL[1] as string, place(0, gh + hh / 2, hz, 0, new THREE.Vector3(hw, hh, hd)));
    // Courses of stone: darker bands across the face, so the wall reads as built, not as a box.
    for (let k = 1; k < 6; k++) kit.add(box, HALL[k % 2 ? 0 : 2] as string, place(0, gh + k * (hh / 6), hz + hd / 2 + 0.005, 0, new THREE.Vector3(hw, 0.05, 0.02)));
    kit.add(box, '#5f6570', place(0, gh + 0.15, hz, 0, new THREE.Vector3(hw + 0.2, 0.3, hd + 0.2)));
    // The roof: two steep slopes of dark slate meeting at a ridge along the hall.
    for (const sd of [-1, 1]) kit.add(box, sd > 0 ? '#323945' : '#272c35', place(0, gh + hh + 0.62, hz + sd * 0.72, 0, new THREE.Vector3(hw + 0.4, 0.14, hd * 0.66), sd * 0.82));
    kit.add(box, '#20252d', place(0, gh + hh + 1.32, hz, 0, new THREE.Vector3(hw + 0.5, 0.14, 0.2)));
    for (let k = 0; k < 7; k++) {
      const x = -hw / 2 + 0.6 + k * ((hw - 1.2) / 6);
      // Buttresses between the windows, and two rows of windows (dark, lit faintly in the lower row).
      kit.add(box, HALL[0] as string, place(x, gh + hh * 0.42, hz + hd / 2 + 0.12, 0, new THREE.Vector3(0.26, hh * 0.84, 0.3)));
      if (k < 6) {
        const wx = x + (hw - 1.2) / 12;
        if (Math.abs(wx) > 0.7) kit.add(box, '#1a1d23', place(wx, gh + 0.95, hz + hd / 2 + 0.01, 0, new THREE.Vector3(0.36, 0.6, 0.04)));
        kit.add(box, '#1a1d23', place(wx, gh + 2.2, hz + hd / 2 + 0.01, 0, new THREE.Vector3(0.3, 0.45, 0.04)));
      }
    }
    // The door, and its lintel trimmed in the Order's red.
    kit.add(box, WOOD[2] as string, place(0, gh + 0.75, hz + hd / 2 + 0.03, 0, new THREE.Vector3(0.9, 1.5, 0.06)));
    kit.add(box, STONE[2] as string, place(0, gh + 1.6, hz + hd / 2 + 0.08, 0, new THREE.Vector3(1.3, 0.22, 0.16)));
    kit.add(box, COMPANY_RED, place(0, gh + 1.6, hz + hd / 2 + 0.17, 0, new THREE.Vector3(1.32, 0.05, 0.02)));
    // Banners either side of the door: long dark cloth with a red band.
    for (const sx of [-1.4, 1.4]) {
      kit.add(box, '#2a2f3a', place(sx, gh + 1.75, hz + hd / 2 + 0.06, 0, new THREE.Vector3(0.5, 1.5, 0.02)));
      kit.add(box, COMPANY_RED, place(sx, gh + 1.35, hz + hd / 2 + 0.075, 0, new THREE.Vector3(0.5, 0.1, 0.02)));
      kit.add(box, WOOD[0] as string, place(sx, gh + 2.52, hz + hd / 2 + 0.08, 0, new THREE.Vector3(0.62, 0.05, 0.05)));
    }
    // Two chimneys.
    for (const sx of [-2.6, 2.4]) kit.add(box, STONE[0] as string, place(sx, gh + hh + 1.5, hz - 0.3, 0, new THREE.Vector3(0.4, 1.4, 0.4)));
    // The tower at the hall's east end: round, a cone of slate, a slit or two.
    const tx = hw / 2 + 0.9;
    const tz = hz + 0.2;
    const tg = this.ctx.ground(tx, tz);
    kit.add(cyl, HALL[1] as string, place(tx, tg + 2.6, tz, 0, new THREE.Vector3(2.0, 5.2, 2.0)));
    const crown = new THREE.ConeGeometry(0.5, 1, 10);
    kit.add(crown, '#2c323c', place(tx, tg + 6.1, tz, 0, new THREE.Vector3(2.5, 1.9, 2.5)));
    crown.dispose();
    for (const y of [1.8, 3.4]) kit.add(box, '#1a1d23', place(tx, tg + y, tz + 1.0, 0, new THREE.Vector3(0.12, 0.5, 0.04)));
    kit.add(box, WOOD[0] as string, place(tx, tg + 7.3, tz, 0, new THREE.Vector3(0.04, 1.2, 0.04)));
    kit.add(box, COMPANY_RED, place(tx + 0.3, tg + 7.65, tz, 0, new THREE.Vector3(0.56, 0.32, 0.02)));
    box.dispose();
    cyl.dispose();
  }

  /** The guard house: burnt stone walls round its yard, the well, the door with the page; or its common room's hearth. */
  private buildGuardhouse(kit: Kit, room: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    // Walls in a broken ring two and a half hexes out, a door at the north.
    const R = 2.6 * S;
    const n = 22;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const door = Math.abs(Math.atan2(Math.sin(a + Math.PI / 2), Math.cos(a + Math.PI / 2))) < 0.2;
      if (door) continue;
      const x = Math.cos(a) * R;
      const z = Math.sin(a) * R;
      const g = this.ctx.ground(x, z);
      const h = room ? 1.0 + (i % 3) * 0.25 : [0.9, 1.4, 0.6, 1.7, 1.1][i % 5]!;
      const yaw = -a - Math.PI / 2;
      kit.add(box, BURNT[i % 3] as string, place(x, g + h / 2, z, yaw, new THREE.Vector3((Math.PI * 2 * R) / n + 0.04, h, 0.36)));
      kit.add(box, SNOW, place(x, g + h + 0.03, z, yaw, new THREE.Vector3((Math.PI * 2 * R) / n - 0.02, 0.06, 0.4)));
      if (i % 4 === 1) kit.add(rock, BURNT[0] as string, place(x * 0.9, g + 0.12, z * 0.9, i, 0.3));
    }
    // The door frame at the north, and on it the page nailed with a knife.
    const dz = -R;
    const g0 = this.ctx.ground(0, dz);
    for (const k of [-1, 1]) kit.add(box, WOOD[1] as string, place(k * 0.48, g0 + 0.95, dz, 0, new THREE.Vector3(0.14, 1.9, 0.16)));
    kit.add(box, WOOD[0] as string, place(0, g0 + 1.92, dz, 0, new THREE.Vector3(1.12, 0.16, 0.18)));
    kit.add(box, WOOD[2] as string, place(0.15, g0 + 0.9, dz + 0.08, 0.25, new THREE.Vector3(0.82, 1.7, 0.06)));
    kit.add(box, '#e4ddd0', place(0.12, g0 + 1.25, dz + 0.13, 0.25, new THREE.Vector3(0.28, 0.36, 0.01)));
    kit.add(box, '#2a2622', place(0.12, g0 + 1.22, dz + 0.16, 0.25, new THREE.Vector3(0.2, 0.01, 0.01)));
    kit.add(box, '#2a2622', place(0.12, g0 + 1.28, dz + 0.16, 0.25, new THREE.Vector3(0.2, 0.01, 0.01)));
    kit.add(box, '#9aa0a8', place(0.12, g0 + 1.38, dz + 0.2, 0.25, new THREE.Vector3(0.03, 0.03, 0.16)));
    kit.add(box, '#2e2622', place(0.12, g0 + 1.38, dz + 0.3, 0.25, new THREE.Vector3(0.04, 0.04, 0.09)));
    this.anchors.rock = new THREE.Vector3(0.12, g0 + 1.25, dz + 0.13);
    if (!room) {
      // The well, in the middle of the yard: a stone ring, a frame and a rope.
      const wx = 0.9;
      const wz = 0.6;
      const gw = this.ctx.ground(wx, wz);
      kit.add(cyl, STONE[0] as string, place(wx, gw + 0.28, wz, 0, new THREE.Vector3(0.9, 0.56, 0.9)));
      kit.add(cyl, '#14161a', place(wx, gw + 0.57, wz, 0, new THREE.Vector3(0.66, 0.02, 0.66)));
      kit.add(cyl, SNOW, place(wx, gw + 0.58, wz, 0, new THREE.Vector3(0.92, 0.04, 0.92)));
      for (const k of [-1, 1]) kit.add(box, WOOD[0] as string, place(wx + k * 0.4, gw + 0.9, wz, 0, new THREE.Vector3(0.07, 1.0, 0.07)));
      kit.add(box, WOOD[1] as string, place(wx, gw + 1.38, wz, 0, new THREE.Vector3(0.9, 0.07, 0.07)));
    } else {
      // The common room: a hearth at the south wall, two burnt bunks, the roof's beams open to the sky.
      const hz = 1.0;
      const gh = this.ctx.ground(0, hz);
      for (let k = 0; k < 9; k++) {
        const t = (k / 9) * Math.PI * 2;
        kit.add(rock, STONE[k % 3] as string, place(Math.cos(t) * 0.42, gh + 0.08, hz + Math.sin(t) * 0.42 - 1, k, 0.2));
      }
      for (const k of [-1, 1]) {
        kit.add(box, BURNT[1] as string, place(k * 1.9, gh + 0.4, -0.6, 0, new THREE.Vector3(0.7, 0.08, 1.6)));
        kit.add(box, BURNT[2] as string, place(k * 1.9, gh + 0.95, -0.6, 0, new THREE.Vector3(0.7, 0.06, 1.6)));
        for (const z of [-1.35, 0.15]) kit.add(box, BURNT[0] as string, place(k * 1.9 + 0.3, gh + 0.6, z, 0, new THREE.Vector3(0.07, 1.2, 0.07)));
      }
      for (let k = 0; k < 4; k++) kit.add(box, BURNT[k % 3] as string, place(-2 + k * 1.3, gh + 2.2, 0, 0.1 * k, new THREE.Vector3(0.12, 0.12, 5.4), 0, 0.1));
    }
    box.dispose();
    cyl.dispose();
    rock.dispose();
  }

  /** The ledge of the wedges: the cliff, the wall and its gaps, the strip and its stakes, the cornice, the rock, the house. */
  private buildLedge(kit: Kit, s: BattleState): void {
    const r = s.rules?.senda?.wedge;
    if (!r) return;
    const R = this.map.radius;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const slab = new THREE.CylinderGeometry(0.42, 0.55, 1, 5);
    const dome = new THREE.SphereGeometry(0.5, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const spire = new THREE.OctahedronGeometry(0.5, 0);
    const centre = (id: number): THREE.Vector3 => {
      const c = cellCenter(R, id);
      return this.w(c.x, c.y);
    };
    const rocks = this.map.rocks ?? [];
    const wallSet = new Set<string>();
    // Which rock hexes are the wall: the ring four out round the house.
    for (const h of rocks) if (Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(-h.q - h.r)) === 4) wallSet.add(`${h.q},${h.r}`);
    const gapIds = new Set(r.gaps);
    let k = 0;
    for (const h of rocks) {
      const p = this.hexAt(h.q, h.r);
      const key = `${h.q},${h.r}`;
      k++;
      const j = (Math.sin(k * 12.9898) * 43758.5453) % 1;
      const jj = Math.abs(j);
      if (wallSet.has(key)) {
        // The yard wall: knee-high dry stone, a cap of snow, along the ring.
        const yaw = -Math.atan2(p.z, p.x) - Math.PI / 2;
        kit.add(box, WALL[k % 3] as string, place(p.x, p.y + 0.24, p.z, yaw, new THREE.Vector3(1.22, 0.48, 0.42)));
        kit.add(box, WALL[(k + 1) % 3] as string, place(p.x, p.y + 0.52, p.z, yaw + 0.03, new THREE.Vector3(1.1, 0.1, 0.46)));
        kit.add(box, SNOW, place(p.x, p.y + 0.6, p.z, yaw, new THREE.Vector3(1.04, 0.07, 0.4)));
        continue;
      }
      // The cliff: dark, broken blue-grey rock in shards and spires, snow only caught on its ledges (no white caps:
      // from the board's camera they read as stumps). Where it stands over the strip, a face high enough to carry
      // the cornice; further out it drops back to boulders, so the board's camera sees over it.
      const face = r.strip.some((sid) => {
        const sc = cellCenter(R, sid);
        const hc = hexToWorld(h.q, h.r);
        return Math.abs(sc.x - hc.x) + Math.abs(sc.y - hc.y) < 1300;
      });
      const out = Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(-h.q - h.r));
      const tall = face ? 2.3 + jj * 0.7 : out >= 7 ? 0.5 + jj * 0.5 : 0.9 + jj * 0.8;
      const yaw0 = jj * 6.28;
      const c0 = CLIFF[k % CLIFF.length] as string;
      const c1 = CLIFF[(k + 1) % CLIFF.length] as string;
      kit.add(rock, CLIFF[3] as string, place(p.x, p.y + 0.14, p.z, k, new THREE.Vector3(1.4, 0.5, 1.3)));
      // The main spire, leaning, and a lower shard beside it.
      kit.add(spire, c0, place(p.x + j * 0.14, p.y + tall * 0.5, p.z - j * 0.12, yaw0, new THREE.Vector3(1.15 + jj * 0.3, tall, 1.0), (j - 0.5) * 0.22, (jj - 0.5) * 0.18));
      kit.add(spire, c1, place(p.x - 0.3 + jj * 0.2, p.y + tall * 0.33, p.z + 0.26 - j * 0.2, yaw0 + 1.3, new THREE.Vector3(0.8, tall * 0.66, 0.75), 0.15, -0.2));
      if (face) kit.add(spire, CLIFF[2] as string, place(p.x + 0.32, p.y + tall * 0.4, p.z - 0.2, yaw0 + 2.4, new THREE.Vector3(0.7, tall * 0.8, 0.6), -0.12, 0.1));
      // Snow caught on a ledge and in the cracks: small, blue in the shade.
      if (k % 3 === 0) kit.add(dome, SNOW_SHADE, place(p.x - j * 0.25, p.y + tall * 0.42, p.z + j * 0.2, k, new THREE.Vector3(0.7, 0.1, 0.5)));
    }
    // The gaps: two flat stones at the foot of each, where a shield goes; the planted shields themselves.
    r.gaps.forEach((id, i) => {
      const p = centre(id);
      this.anchors.gaps[i] = p.clone().setY(p.y + 0.9);
      const yaw = -Math.atan2(p.z, p.x) - Math.PI / 2;
      kit.add(box, '#5d636c', place(p.x, p.y + 0.03, p.z, yaw, new THREE.Vector3(0.9, 0.06, 0.36)));
      const g = new THREE.Group();
      g.position.copy(p);
      g.rotation.y = Math.atan2(p.x, p.z);
      const sh = companyShield(g, 1.25);
      sh.position.y = 0.36;
      g.visible = false;
      this.group.add(g);
      this.gapShields.push(g);
    });
    void gapIds;
    // The strip: trodden snow, and stakes with red rags at its two ends (the marked stretch under the cornice).
    const ends = [r.strip[0] as number, r.strip[r.strip.length - 1] as number];
    for (const id of r.strip) {
      const p = centre(id);
      kit.add(new THREE.CylinderGeometry(0.66, 0.66, 0.02, 6), new THREE.Color('#aab6c6'), place(p.x, p.y + 0.012, p.z, Math.PI / 6, 1));
    }
    for (const id of ends) {
      const p = centre(id);
      const toHouse = Math.atan2(-p.x, -p.z);
      for (const side of [-1, 1]) {
        const x = p.x + Math.cos(toHouse) * 0.45 * side;
        const z = p.z - Math.sin(toHouse) * 0.45 * side;
        kit.add(box, WOOD[0] as string, place(x, p.y + 0.48, z, 0, new THREE.Vector3(0.06, 0.96, 0.06)));
        kit.add(box, COMPANY_RED, place(x + 0.09, p.y + 0.86, z, 0.4, new THREE.Vector3(0.2, 0.12, 0.02)));
        kit.add(box, COMPANY_RED, place(x + 0.16, p.y + 0.78, z, 0.7, new THREE.Vector3(0.12, 0.08, 0.02), 0, 0.5));
      }
    }
    const mid = centre(r.strip[1] as number);
    this.stripMid.copy(mid);
    // The cornice: a lip of packed snow hanging from the cliff face out over the strip, its length, curling down.
    const nook = centre(r.nook);
    const away = new THREE.Vector3(mid.x - nook.x, 0, mid.z - nook.z).normalize();
    const cg = new THREE.Group();
    cg.name = 'cornice';
    const lipM = new THREE.MeshStandardMaterial({ color: SNOW, roughness: 0.9, flatShading: true });
    this.mats.push(lipM);
    const lip = new THREE.SphereGeometry(0.5, 10, 6);
    this.geos.push(lip);
    const pts = r.strip.map((id) => centre(id));
    // One merged mesh, so it comes down as one: a thick slab of packed snow rooted on the cliff's face and leaning out
    // over the strip, its top bright, its belly blue in its own shade, a curled lip, icicles along its front and a
    // dark crack on top where it will break. From the board's camera it has to read as a weight hanging over the way.
    const ck = new Kit();
    const side = new THREE.Vector3(-away.z, 0, away.x);
    const n = pts.length * 3 + 1;
    const icicle = new THREE.ConeGeometry(0.5, 1, 5);
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const fu = u * (pts.length - 1);
      const a = pts[Math.min(pts.length - 1, Math.floor(fu))] as THREE.Vector3;
      const b = pts[Math.min(pts.length - 1, Math.ceil(fu))] as THREE.Vector3;
      const at = a.clone().lerp(b, fu - Math.floor(fu)).sub(mid);
      // Thicker and further out in the middle, thinning to its two ends.
      const mid01 = Math.sin(u * Math.PI);
      const reach = 0.25 + mid01 * 0.35;
      const wob = Math.sin(i * 2.3) * 0.05;
      // Each piece lies across the strip (its length from the cliff out), overlapping the next along it.
      const yaw = Math.atan2(away.x, away.z);
      const step = (pts.length - 1) * S / (n - 1) * 1.9 + 0.3;
      const root = at.clone().addScaledVector(away, 0.95);
      const tip = at.clone().addScaledVector(away, -reach);
      const c = root.clone().lerp(tip, 0.5);
      const len = root.distanceTo(tip) + 0.5;
      // The belly (blue, in its own shade) under the top (bright).
      ck.add(lip, CORNICE_BELLY, place(c.x, 2.2 + wob, c.z, yaw, new THREE.Vector3(step, 0.46, len), -0.12, 0));
      ck.add(lip, SNOW, place(c.x + away.x * 0.06, 2.42 + wob, c.z + away.z * 0.06, yaw, new THREE.Vector3(step * 1.02, 0.36, len * 0.98), -0.08, 0));
      // The curled lip at its front edge, one roll along it.
      ck.add(lip, SNOW_SHADE, place(tip.x, 2.18 + wob, tip.z, yaw, new THREE.Vector3(step * 1.05, 0.34, 0.4)));
      // Icicles under the lip, of different lengths.
      if (i % 2 === 0) {
        const il = 0.25 + Math.abs(Math.sin(i * 5.1)) * 0.4;
        ck.add(icicle, '#cfe0f2', place(tip.x + side.x * 0.1, 2.02 - il / 2, tip.z + side.z * 0.1, 0, new THREE.Vector3(0.09, il, 0.09), Math.PI, 0));
      }
    }
    // The crack along its top, near the root: where it breaks.
    for (let i = 1; i < n - 1; i++) {
      const u = i / (n - 1);
      const fu = u * (pts.length - 1);
      const a = pts[Math.min(pts.length - 1, Math.floor(fu))] as THREE.Vector3;
      const b = pts[Math.min(pts.length - 1, Math.ceil(fu))] as THREE.Vector3;
      const at = a.clone().lerp(b, fu - Math.floor(fu)).sub(mid).addScaledVector(away, 0.55 + Math.sin(i * 3.7) * 0.06);
      ck.add(lip, '#33405a', place(at.x, 2.62, at.z, Math.atan2(side.x, side.z) + Math.sin(i * 1.9) * 0.4, new THREE.Vector3(0.05, 0.05, 0.42)));
    }
    icicle.dispose();
    const cm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0, flatShading: true });
    this.mats.push(cm);
    const cmesh = ck.build(cm, 'cornice');
    if (cmesh) {
      cmesh.castShadow = true;
      this.geos.push(cmesh.geometry);
      cg.add(cmesh);
    }
    cg.position.copy(mid);
    this.cornice = cg;
    this.group.add(cg);
    // Its shade on the strip, always (the danger under it): a cold band along the way.
    const band: number[] = [];
    const bidx: number[] = [];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i] as THREE.Vector3;
      const g = this.ctx.ground(p.x, p.z) + 0.03;
      const lo = p.clone().addScaledVector(away, -0.45);
      const hi = p.clone().addScaledVector(away, 0.7);
      const ext = i === 0 ? -0.5 : i === pts.length - 1 ? 0.5 : 0;
      const along = pts.length > 1 ? (pts[pts.length - 1] as THREE.Vector3).clone().sub(pts[0] as THREE.Vector3).normalize() : side;
      band.push(lo.x + along.x * ext, g, lo.z + along.z * ext, hi.x + along.x * ext, g, hi.z + along.z * ext);
      if (i > 0) {
        const q = (i - 1) * 2;
        bidx.push(q, q + 1, q + 2, q + 1, q + 3, q + 2);
      }
    }
    const bg = new THREE.BufferGeometry();
    bg.setAttribute('position', new THREE.Float32BufferAttribute(band, 3));
    bg.setIndex(bidx);
    this.geos.push(bg);
    const bm = new THREE.MeshBasicMaterial({ color: '#1d2b46', transparent: true, opacity: 0.3, depthWrite: false, side: THREE.DoubleSide });
    this.mats.push(bm);
    const bandMesh = new THREE.Mesh(bg, bm);
    bandMesh.renderOrder = 3;
    bandMesh.name = 'cornice-shade';
    this.coShade = bandMesh;
    this.group.add(bandMesh);
    // The counter sits on the cornice itself, at the end of it away from the rock's mark.
    const sill0 = new THREE.Vector3().addVectors(mid, nook).multiplyScalar(0.5);
    const e0 = pts[0] as THREE.Vector3;
    const e1 = pts[pts.length - 1] as THREE.Vector3;
    const far = e0.distanceToSquared(sill0) > e1.distanceToSquared(sill0) ? e0 : e1;
    this.anchors.cornice = far.clone().addScaledVector(away, 0.3).setY(mid.y + 2.75);
    // The loose rock: a boulder on the wall's stone between the yard and the strip, under the lip, cracked.
    const sill = new THREE.Vector3().addVectors(mid, nook).multiplyScalar(0.5);
    void nook;
    const lr = new THREE.Mesh(rock, new THREE.MeshStandardMaterial({ color: '#59616d', roughness: 0.9, flatShading: true }));
    this.mats.push(lr.material as THREE.Material);
    lr.position.set(sill.x, sill.y + 0.95, sill.z);
    lr.scale.set(0.95, 0.75, 0.85);
    lr.castShadow = true;
    this.loose = lr;
    this.group.add(lr);
    // Its mark sits on the yard's side of it, where the hero pushes from.
    this.anchors.rock = nook.clone().lerp(sill, 0.35).setY(sill.y + 0.5);
    // Under the rock, a propped beam and the crack.
    kit.add(box, WOOD[1] as string, place(sill.x, sill.y + 0.45, sill.z, Math.atan2(away.x, away.z), new THREE.Vector3(0.1, 0.9, 0.1), 0.3));
    // The shadow the rock and the lip throw on the strip as they come (shown after the push, growing).
    const shM = new THREE.MeshBasicMaterial({ color: '#0d1420', transparent: true, opacity: 0, depthWrite: false });
    this.mats.push(shM);
    const disc = new THREE.CircleGeometry(1, 24);
    this.geos.push(disc);
    const sh = new THREE.Mesh(disc, shM);
    sh.rotation.x = -Math.PI / 2;
    sh.position.set(mid.x, mid.y + 0.05, mid.z);
    sh.scale.set(1.8, 1.0, 1);
    sh.renderOrder = 3;
    this.shadow = sh;
    this.shadowMat = shM;
    this.group.add(sh);
    // The snow fallen: a long mound over the strip, shown when the cornice comes down.
    const pile = new THREE.Mesh(lip, lipM);
    pile.position.set(mid.x, mid.y + 0.1, mid.z);
    pile.scale.set(2.6, 0.55, 1.5);
    const along = (pts[pts.length - 1] as THREE.Vector3).clone().sub(pts[0] as THREE.Vector3);
    pile.rotation.y = Math.atan2(along.x, along.z) + Math.PI / 2;
    pile.visible = false;
    this.fallen = pile;
    this.group.add(pile);
    // Old boot prints in the snow (Harl's, the story says later): out from the wall by the planted gap farthest from
    // the cornice, then up the slope toward the ledge, pressed in pairs, fading as they go.
    // (Planted ones first: a scene after the fight has them in; the fight starts with none.)
    const shut = s.senda?.wedge?.shut ?? [];
    const allGaps = s.rules?.senda?.wedge?.gaps ?? [];
    const sol = (shut.some((x) => x === 1) ? allGaps.filter((_, i) => shut[i] === 1) : allGaps).map((id) => centre(id));
    if (sol.length) {
      const g0 = sol.reduce((a, b) => (a.distanceToSquared(mid) > b.distanceToSquared(mid) ? a : b));
      const a0 = Math.atan2(g0.z, g0.x);
      const am = Math.atan2(mid.z, mid.x);
      let turn = am - a0;
      while (turn > Math.PI) turn -= Math.PI * 2;
      while (turn < -Math.PI) turn += Math.PI * 2;
      const way = new THREE.CatmullRomCurve3(
        [
          [4.55, 0],
          [5.3, 0.06],
          [6.0, 0.22],
          [6.7, 0.42],
        ].map(([rr, k]) => {
          const a = a0 + turn * (k as number);
          const x = Math.cos(a) * (rr as number) * S;
          const z = Math.sin(a) * (rr as number) * S;
          return new THREE.Vector3(x, this.ctx.ground(x, z), z);
        }),
      );
      const print = new THREE.CylinderGeometry(0.5, 0.5, 0.02, 7);
      const len = way.getLength();
      const n = Math.floor(len / 0.32);
      const p = new THREE.Vector3();
      const t = new THREE.Vector3();
      for (let i = 0; i < n; i++) {
        way.getPointAt(i / n, p);
        way.getTangentAt(i / n, t);
        const side = i % 2 ? 1 : -1;
        const fade = 1 - (i / n) * 0.55;
        const c = SNOW_SHADE.clone().lerp(new THREE.Color('#8e9bb0'), 0.7 * fade);
        kit.add(print, c, place(p.x - t.z * 0.09 * side, this.ctx.ground(p.x, p.z) + 0.015, p.z + t.x * 0.09 * side, Math.atan2(t.x, t.z), new THREE.Vector3(0.11, 1, 0.2)));
      }
      print.dispose();
    }
    // The guard house in the middle: burnt stone, a snow-laden roof, its door to the yard's open side.
    const hz = this.w(0, 0);
    this.anchors.house = hz.clone().setY(hz.y + 2.2);
    const yaw = Math.atan2(mid.x, mid.z) + Math.PI;
    const hp = (lx: number, ly: number, lz: number): [number, number, number] => [hz.x + Math.cos(yaw) * lx + Math.sin(yaw) * lz, hz.y + ly, hz.z - Math.sin(yaw) * lx + Math.cos(yaw) * lz];
    const add = (geo: THREE.BufferGeometry, col: THREE.ColorRepresentation, lx: number, ly: number, lz: number, sc: THREE.Vector3, extraYaw = 0, pitch = 0, roll = 0): void => {
      const [x, y, z] = hp(lx, ly, lz);
      kit.add(geo, col, place(x, y, z, yaw + extraYaw, sc, pitch, roll));
    };
    add(box, BURNT[1] as string, 0, 0.62, 0, new THREE.Vector3(1.6, 1.24, 1.3));
    add(box, STONE[1] as string, 0, 0.08, 0, new THREE.Vector3(1.7, 0.16, 1.4));
    for (const side of [-1, 1]) add(box, WOOD[2] as string, side * 0.42, 1.48, 0, new THREE.Vector3(0.95, 0.08, 1.5), 0, 0, side * 0.62);
    for (const side of [-1, 1]) add(box, SNOW, side * 0.44, 1.54, 0, new THREE.Vector3(0.9, 0.06, 1.44), 0, 0, side * 0.62);
    add(box, '#14161a', 0, 0.45, 0.66, new THREE.Vector3(0.42, 0.78, 0.02));
    add(box, WOOD[1] as string, 0, 0.88, 0.67, new THREE.Vector3(0.55, 0.07, 0.04));
    add(box, '#1a1c22', 0.5, 0.85, 0.66, new THREE.Vector3(0.18, 0.18, 0.02));
    // A chimney, cold.
    add(box, BURNT[0] as string, -0.45, 1.75, -0.3, new THREE.Vector3(0.22, 0.6, 0.22));
    box.dispose();
    slab.dispose();
    dome.dispose();
    rock.dispose();
    spire.dispose();
  }

  /* ---------- as the fight goes ---------- */

  tick(s: BattleState): void {
    this.chasm?.tick(s);
    this.fort?.tick(s);
    this.slide?.tick(s);
    this.pass?.tick(s);
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.w(e.x, e.y);
      if (e.kind === 'plant' || e.kind === 'pull') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.2), { n: this.ctx.low ? 6 : 14, color: '#f2f6ff', speed: 0.04, up: 0.05, size: 0.12, life: 18, gravity: 0.003, spread: 0.4 });
        this.ctx.shake(0.05);
      } else if (e.kind === 'push') {
        this.pushAt = this.now;
        if (this.loose) this.ctx.vfx.burst(this.loose.position.clone(), { n: 8, color: '#8a929e', speed: 0.03, up: 0.02, size: 0.1, life: 16, gravity: 0.004, spread: 0.3 });
      } else if (e.kind === 'fall') {
        this.fallAt = this.now;
        const m = this.stripMid;
        this.ctx.shake(0.55);
        this.ctx.vfx.burst(m.clone().setY(m.y + 0.6), { n: this.ctx.low ? 24 : 60, color: '#f4f8ff', speed: 0.12, up: 0.09, size: 0.35, life: 34, gravity: 0.004, spread: 1.6, smoke: true });
        this.ctx.vfx.burst(m.clone().setY(m.y + 0.3), { n: this.ctx.low ? 12 : 28, color: '#dfe7f2', speed: 0.18, up: 0.04, size: 0.18, life: 26, gravity: 0.006, spread: 1.2 });
        this.ctx.flare(m.clone().setY(m.y + 1), '#ffffff', 6, 8);
      } else if (e.kind === 'enter') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: 10, color: '#ff4f3a', speed: 0.04, up: 0.04, size: 0.12, life: 18, gravity: 0.002, spread: 0.4 });
      } else if (e.kind === 'work' || e.kind === 'drop') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.15), { n: 8, color: '#eef3fa', speed: 0.05, up: 0.06, size: 0.1, life: 16, gravity: 0.004, spread: 0.3 });
      }
    }
  }

  /** Every drawn frame: the shields in the gaps and in the snow, the way on the ground, the rock and its shadow. */
  draw(s: BattleState): void {
    this.chasm?.draw(s);
    this.fort?.draw(s);
    this.slide?.draw(s);
    this.pass?.draw(s);
    const st = s.senda;
    if (this.dig.length && st) {
      const r = s.rules?.senda;
      const i = r?.npcs.findIndex((n) => n.does === 'work') ?? -1;
      const kel = i >= 0 ? st.npcs[i] : undefined;
      // Dug ones go onto the stretcher (hidden here); the one being dug rocks a little as the spade works.
      this.dig.forEach((d, k) => {
        d.g.visible = !kel || k >= kel.spot;
        const c = this.carried[k];
        if (c) c.visible = !!kel && k < kel.spot;
        if (kel && k === kel.spot && kel.held > 0) d.g.rotation.z = Math.sin(this.now * 9) * 0.05 * Math.min(1, kel.held / 60);
      });
    }
    const w = st?.wedge;
    const r = s.rules?.senda?.wedge;
    if (!w || !r) return;
    this.gapShields.forEach((g, i) => (g.visible = w.shut[i] === 1));
    // The way: the column's path from the rim to the house, redrawn as soon as a shield goes in or out.
    const showWay = !w.down;
    const key = `${s.gridRev}|${showWay}`;
    if (key !== this.wayKey) {
      this.wayKey = key;
      this.buildWay(s, showWay ? wedgeRoute(s) : [], w.narrow);
    }
    if (this.wayMat) this.wayMat.opacity = 0.78 + 0.15 * Math.sin(this.now * 3.2);
    // The rock: rocking after the push, then over the edge; the shadow growing on the strip; the cornice coming down.
    const lr = this.loose;
    const cg = this.cornice;
    if (w.pushed >= 0 && !w.down && lr && this.shadowMat && this.shadow) {
      const k = this.pushAt >= 0 ? Math.min(1, (this.now - this.pushAt) / 3) : 0.5;
      lr.rotation.z = Math.sin(this.now * 14) * 0.06 * (1 - k) + k * 0.5;
      this.shadowMat.opacity = 0.18 + 0.42 * k;
      this.shadow.scale.set(1.2 + 1.0 * k, 0.8 + 0.5 * k, 1);
    }
    if (w.down) {
      if (this.shadowMat) this.shadowMat.opacity = 0;
      const t = this.fallAt >= 0 ? Math.min(1, (this.now - this.fallAt) / 0.45) : 1;
      if (cg) {
        cg.position.y = this.stripMid.y - 1.8 * t * t;
        cg.visible = t < 1;
      }
      if (lr) lr.visible = false;
      if (this.coShade) this.coShade.visible = t < 1;
      if (this.fallen) {
        this.fallen.visible = t > 0.6;
        this.fallen.scale.y = 0.55 * Math.min(1, (t - 0.6) * 3);
      }
    }
  }

  /** The column's way as a red ribbon on the ground (wider and brighter where it is one hex wide). */
  private buildWay(s: BattleState, route: number[], narrow: number[]): void {
    if (this.way) {
      this.group.remove(this.way);
      this.way.geometry.dispose();
      this.way = null;
    }
    if (route.length < 2) return;
    const R = this.map.radius;
    const pts = route.map((id) => {
      const c = cellCenter(R, id);
      return this.w(c.x, c.y, 0.06);
    });
    const curve = new THREE.CatmullRomCurve3(pts, false, 'centripetal', 0.3);
    const n = route.length * 6;
    const pos: number[] = [];
    const col: number[] = [];
    const idx: number[] = [];
    const nar = new Set(narrow);
    const p = new THREE.Vector3();
    const t = new THREE.Vector3();
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      curve.getPointAt(u, p);
      curve.getTangentAt(u, t);
      const cell = route[Math.min(route.length - 1, Math.round(u * (route.length - 1)))] as number;
      const wide = nar.has(cell);
      const half = wide ? 0.13 : 0.075;
      const sx = -t.z;
      const sz = t.x;
      const l = Math.hypot(sx, sz) || 1;
      const y = this.ctx.ground(p.x, p.z) + 0.07;
      pos.push(p.x + (sx / l) * half, y, p.z + (sz / l) * half, p.x - (sx / l) * half, y, p.z - (sz / l) * half);
      // Solid red where it narrows; dashed (red and the snow's own pale) over the open ground.
      const dash = wide || Math.floor(u * n * 0.5) % 2 === 0;
      const c = dash ? (wide ? RED : RED.clone().lerp(RED_SOFT, 0.25)) : SNOW;
      col.push(c.r, c.g, c.b, c.r, c.g, c.b);
      if (i < n) {
        const a = i * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.setIndex(idx);
    if (!this.wayMat) {
      this.wayMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide, fog: false });
      this.mats.push(this.wayMat);
    }
    this.way = new THREE.Mesh(geo, this.wayMat);
    this.way.renderOrder = 4;
    this.way.name = 'wedge-way';
    this.group.add(this.way);
    void s;
  }

  frame(now: number, camera?: THREE.Camera): void {
    this.now = now;
    this.dome?.frame(now, camera);
    this.fall?.frame(now, camera);
    this.chasm?.frame(now);
    this.fort?.frame(now);
    this.slide?.frame(now);
    this.pass?.frame(now);
    for (const m of this.winds) (m.uniforms.time as THREE.IUniform<number>).value = now;
  }

  dispose(): void {
    this.chasm?.dispose();
    this.fort?.dispose();
    this.slide?.dispose();
    this.pass?.dispose();
    this.dome?.dispose();
    this.fall?.dispose();
    if (this.way) this.way.geometry.dispose();
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
  }
}

/**
 * The guard house (the warrior's chapter 1, act 2): the crypt re-dressed as the Company's burnt guard house half way
 * up the Spine. Four rooms down: the fallen entrance (charred beams across the floor), the dormitory (bunks along the
 * walls), the armoury (empty racks), and the well's yard (the well, and at its far side the door with the first page
 * of Ruy's diary nailed to it with a knife). Burnt stone walls with snow on their tops; the light is the day's,
 * cold, falling through the burnt roof (no sconces), and snow drifted in along the walls.
 *
 * Draw calls: the dressing (one merged mesh). No lights of its own: the rooms' are repainted to daylight.
 */
export class GuardhouseCrypt {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: { mesh: THREE.Mesh; runes: THREE.Group; flames: { mesh: THREE.Object3D; glow: THREE.Sprite }[]; decor: THREE.Group },
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'guardhouse';
    const L = map.dungeon;
    crypt.runes.visible = false;
    for (const f of crypt.flames) {
      f.mesh.visible = false;
      f.glow.visible = false;
    }
    rooms.forEach((l) => {
      l.color.set(0xcfdaec);
      l.intensity = 11;
    });
    // Burnt stone, snow on the tops of the walls and drifted against their feet.
    const geo = crypt.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (col && pos) {
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const n = 0.5 + 0.5 * Math.sin(x * 2.3 + z * 1.3) * Math.cos(z * 1.9 - x * 0.7);
        if (y > 0.5) c.copy(SNOW).lerp(SNOW_SHADE, n * 0.5);
        else if (y > 0.05) c.set(BURNT[Math.floor(n * 2.99)] as string).multiplyScalar(0.9 + lum * 0.8);
        else if (lum < 0.04) c.set('#1e2228');
        else c.set(STONE[Math.floor(n * 2.99)] as string).multiplyScalar(0.42 + lum * 0.4).lerp(SNOW, n > 0.86 ? 0.35 : 0);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    if (!L) return;
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const dome = new THREE.SphereGeometry(0.5, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
    const at = (q: number, r: number): THREE.Vector3 => {
      const c = hexToWorld(q, r);
      return this.ctx.at(c.x, c.y);
    };
    L.rooms.forEach((room, i) => {
      const c = at(room.q, room.r);
      const rr = (room.radius - 0.6) * S;
      // Snow drifted in along the room's wall, in heaps.
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2 + i;
        kit.add(dome, k % 2 ? SNOW_SHADE : '#9aa6b6', place(c.x + Math.cos(a) * rr, c.y - 0.02, c.z + Math.sin(a) * rr, a, new THREE.Vector3(0.8, 0.14, 0.45)));
      }
      if (i === 0) {
        // The fallen entrance: charred beams across the floor.
        for (let k = 0; k < 4; k++) kit.add(box, BURNT[k % 3] as string, place(c.x + (k - 1.5) * 0.5, c.y + 0.08, c.z + Math.sin(k) * 0.6, 0.6 + k * 0.7, new THREE.Vector3(2.2, 0.14, 0.16), 0, 0.12 * k));
      } else if (i === 1) {
        // The dormitory: bunks against the wall, burnt.
        for (let k = 0; k < 4; k++) {
          const a = (k / 4) * Math.PI * 2 + 0.4;
          const x = c.x + Math.cos(a) * (rr - 0.4);
          const z = c.z + Math.sin(a) * (rr - 0.4);
          const yaw = -a;
          kit.add(box, BURNT[1] as string, place(x, c.y + 0.32, z, yaw, new THREE.Vector3(0.6, 0.07, 1.4)));
          kit.add(box, BURNT[2] as string, place(x, c.y + 0.82, z, yaw, new THREE.Vector3(0.6, 0.06, 1.4)));
          kit.add(box, BURNT[0] as string, place(x + Math.cos(a) * 0.25, c.y + 0.55, z + Math.sin(a) * 0.25, yaw, new THREE.Vector3(0.06, 1.1, 0.06)));
        }
      } else if (i === 2) {
        // The armoury: racks, empty, and a shield's rim left on the floor.
        for (let k = 0; k < 3; k++) {
          const a = (k / 3) * Math.PI * 2 + 0.9;
          const x = c.x + Math.cos(a) * (rr - 0.3);
          const z = c.z + Math.sin(a) * (rr - 0.3);
          kit.add(box, WOOD[k % 3] as string, place(x, c.y + 0.55, z, -a, new THREE.Vector3(0.1, 1.1, 1.2)));
          for (let j = 0; j < 3; j++) kit.add(box, WOOD[1] as string, place(x, c.y + 0.3 + j * 0.3, z, -a, new THREE.Vector3(0.14, 0.05, 1.25)));
        }
      } else if (i === L.rooms.length - 1) {
        // The well's yard: the well, and the door at its far side with the page nailed on.
        kit.add(cyl, STONE[0] as string, place(c.x, c.y + 0.3, c.z, 0, new THREE.Vector3(1.0, 0.6, 1.0)));
        kit.add(cyl, '#101216', place(c.x, c.y + 0.61, c.z, 0, new THREE.Vector3(0.74, 0.02, 0.74)));
        kit.add(cyl, SNOW, place(c.x, c.y + 0.62, c.z, 0, new THREE.Vector3(1.02, 0.04, 1.02)));
        const prev = L.rooms[i - 1];
        const back = prev ? Math.atan2(c.z - at(prev.q, prev.r).z, c.x - at(prev.q, prev.r).x) : 0;
        const dx = c.x + Math.cos(back) * (rr - 0.1);
        const dz = c.z + Math.sin(back) * (rr - 0.1);
        const yaw = -back + Math.PI / 2;
        kit.add(box, WOOD[2] as string, place(dx, c.y + 0.9, dz, yaw, new THREE.Vector3(0.9, 1.8, 0.08)));
        for (const k of [-1, 1]) kit.add(box, WOOD[1] as string, place(dx + Math.cos(yaw) * 0.5 * k, c.y + 0.95, dz - Math.sin(yaw) * 0.5 * k, yaw, new THREE.Vector3(0.12, 1.9, 0.14)));
        kit.add(box, '#e4ddd0', place(dx - Math.cos(back) * 0.06, c.y + 1.2, dz - Math.sin(back) * 0.06, yaw + 0.1, new THREE.Vector3(0.3, 0.38, 0.01)));
        kit.add(box, '#9aa0a8', place(dx - Math.cos(back) * 0.14, c.y + 1.34, dz - Math.sin(back) * 0.14, yaw, new THREE.Vector3(0.03, 0.03, 0.18)));
      }
    });
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.94, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const m = kit.build(mat, 'guardhouse-dress');
    if (m) {
      m.castShadow = true;
      m.receiveShadow = true;
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    box.dispose();
    cyl.dispose();
    dome.dispose();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}
